import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { createKeyboardNav, backspacePrev, arrowNav, handleSimpleFormNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Printer, Wallet, Layers, ArrowRight, ArrowLeft, CornerDownLeft, Minimize2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMinimized } from "@/context/MinimizedContext";
import { ItemCombobox } from "@/components/ItemCombobox";
import { PaymentPanel, openTxnPdf } from "@/components/PaymentPanel";
import { ItemRegisterView } from "@/components/ItemRegisterView";
import { useI18n } from "@/context/I18nContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const todayISO = () => new Date().toISOString().slice(0, 10);
const round2 = (n) => Math.round(n * 100) / 100;
const statusColor = {
  Unpaid: "text-red-500 bg-red-500/10 border-red-500/40",
  "Partially Paid": "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  Paid: "text-green-500 bg-green-500/10 border-green-500/40",
};

const emptyBatchPick = { batch_id: "", batch_number: "", original_batch_number: "", qty: 0, mrp: 0, per_unit_cost: 0, cgst: 9, sgst: 9, igst: 0, expiry_date: "", available: 0, serial_numbers: [] };
const emptyLine = { item_id: "", qty: 1, rate: 0, discount_pct: 0, cgst: 9, sgst: 9, igst: 0, batches: [], batches_filled: false };
const emptyForm = () => ({ party_id: "", party_name: "", party_phone: "", party_address: "", invoice_number: "", invoice_override: false, tax_type: "GST", bill_calc: "Exclusive", store_id: "", txn_date: todayISO(), rate_type: "mrp", lines: [{ ...emptyLine }], overall_discount: 0, overall_discount_type: "amt", notes: "" });

export default function SaleTransactions({ type, label, onFullScreenChange }) {
  const { user } = useAuth();
  const { settings: appSettings } = useI18n();
  const { minimize, consumeRestore, pending } = useMinimized();
  const canModify = ["owner", "manager"].includes(user.role);
  const isRetail = type === "retail_sale";
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stores, setStores] = useState([]);
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const pg = usePagination(list, q);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const formRef = useRef(form); formRef.current = form;
  const [batchIdx, setBatchIdx] = useState(null);
  const [stockBatches, setStockBatches] = useState([]);
  const [warrantySerials, setWarrantySerials] = useState({}); // { batch_number: [{serial_number,id}] }
  const [custInfo, setCustInfo] = useState(null);
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [cust, setCust] = useState({ name: "", phone: "", gstin: "", address: "", credit_limit: 0 });
  const [payDlg, setPayDlg] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", note: "", paid_on: todayISO() });
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [regItemId, setRegItemId] = useState(null);
  const [savedDoc, setSavedDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);

  const load = () => {
    const p = new URLSearchParams({ type });
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (q) p.set("q", q);
    api.get(`/transactions?${p.toString()}`).then(r => setList(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [type, dateFrom, dateTo, q]); // eslint-disable-line
  useEffect(() => {
    api.get("/organisation").then(r => { const d = r.data; setOrg(Array.isArray(d) ? (d[0] || null) : d); }).catch(() => {});
  }, []);

  const PAY_LABEL = { Unpaid: "Pending Payment", "Partially Paid": "Partially Paid", Paid: "Paid" };

  const txnTotals = useMemo(() => list.reduce((a, t) => {
    const gst = (t.cgst_total || 0) + (t.sgst_total || 0);
    const amt = (t.total || 0) - gst;
    return {
      items: a.items + (t.lines?.length || 0),
      amount: a.amount + amt, gst: a.gst + gst, total: a.total + (t.total || 0),
      cash: a.cash + (t.paid_cash || 0), phonepay: a.phonepay + (t.paid_phonepay || 0),
      paytm: a.paytm + (t.paid_paytm || 0),
      paid: a.paid + (t.paid_amount || 0), balance: a.balance + (t.outstanding || 0),
    };
  }, { items: 0, amount: 0, gst: 0, total: 0, cash: 0, phonepay: 0, paytm: 0, paid: 0, balance: 0 }), [list]);

  const exportPdf = async () => {
    try {
      const token = localStorage.getItem("erp_token");
      const p = new URLSearchParams({ type });
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (q) p.set("q", q);
      const res = await fetch(`${API}/transactions/export.pdf?${p.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${type}_export.pdf`; a.click();
    } catch { toast.error("PDF export failed"); }
  };
  const printList = () => {
    document.body.classList.add("printing-sale-list");
    window.onafterprint = () => document.body.classList.remove("printing-sale-list");
    window.print();
  };
  const filterSummary = `Period: ${dateFrom || "*"} to ${dateTo || "*"} · Search: ${q || "—"}`;
  useEffect(() => { onFullScreenChange?.(open); }, [open]); // eslint-disable-line
  const prevOpen = useRef(false);
  const prevBatch = useRef(null);
  useEffect(() => {
    const justOpened = open && !prevOpen.current;
    const enteredBatch = open && batchIdx !== null && prevBatch.current === null;
    prevOpen.current = open;
    prevBatch.current = batchIdx;
    if (!justOpened && !enteredBatch) return;
    const t = setTimeout(() => {
      const container = document.querySelector('[data-testid="sale-form-page"]');
      if (!container) return;
      const first = container.querySelector('[role="combobox"], input:not([disabled]), button[role="checkbox"]');
      if (first) first.focus();
    }, 120);
    return () => clearTimeout(t);
  }, [open, batchIdx]);
  useEffect(() => {
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/stores").then(r => setStores(r.data.filter(s => s.status === "Active"))).catch(() => {});
  }, []);

  const pickCustomer = async (cid) => {
    const v = customers.find(x => x.id === cid);
    setForm(f => ({ ...f, party_id: cid, party_name: v?.name || "", party_phone: v?.phone || "", party_address: v?.address || "" }));
    if (cid) {
      try { const { data } = await api.get(`/customers/${cid}/outstanding`); setCustInfo(data); }
      catch { setCustInfo(null); }
    } else setCustInfo(null);
  };

  // Mobile Number search: exact phone match in Customer Master auto-selects the
  // customer; no match keeps the manually typed number (walk-in).
  const onMobileChange = (v) => {
    const digits = v.replace(/[^\d+]/g, "");
    const m = customers.find(c => (c.phone || "") === digits);
    if (m) { if (m.id !== form.party_id) pickCustomer(m.id); return; }
    setForm(f => {
      const sel = customers.find(c => c.id === f.party_id);
      return { ...f, party_phone: digits, ...(sel && (sel.phone || "") !== digits ? { party_id: "" } : {}) };
    });
  };

  // Rate Master rate-type: re-price every item line from the chosen rate; lines stay editable.
  const applyRateType = (rt) => {
    setForm(f => ({
      ...f, rate_type: rt,
      lines: f.lines.map(l => {
        if (!l.item_id) return l;
        const it = items.find(x => x.id === l.item_id);
        if (!it) return l;
        return { ...l, rate: Number(it[rt]) || Number(it.mrp) || Number(it.per_unit_cost) || 0 };
      }),
    }));
  };

  const saveCustomer = async () => {
    try {
      const r = await api.post("/customers", cust);
      toast.success("Customer added");
      const { data } = await api.get("/customers"); setCustomers(data);
      setAddCustOpen(false);
      setCust({ name: "", phone: "", gstin: "", address: "", credit_limit: 0 });
      pickCustomer(r.data.id);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setLineItem = (i, itemId) => {
    const it = items.find(x => x.id === itemId);
    setForm(f => {
      const lines = [...f.lines];
      lines[i] = { ...lines[i], item_id: itemId, rate: it?.mrp || 0,
        cgst: it?.cgst ?? 9, sgst: it?.sgst ?? 9, igst: 0, discount_pct: 0,
        batches: [], batches_filled: false };
      return { ...f, lines };
    });
  };
  const setLineQty = (i, v) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], qty: v, batches_filled: false };
    return { ...f, lines };
  });
  const setLineField = (i, k, v) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], [k]: v };
    return { ...f, lines };
  });
  const lineAmount = (l) => {
    const gross = (Number(l.rate) || 0) * (Number(l.qty) || 0);
    const afterDisc = Math.max(0, gross - gross * (Number(l.discount_pct) || 0) / 100);
    let tax = 0;
    const gstPct = form.tax_type === "GST" ? (Number(l.cgst) || 0) + (Number(l.sgst) || 0)
                : form.tax_type === "IGST" ? Number(l.igst) || 0 : 0;
    if (form.bill_calc === "Inclusive" && gstPct > 0) {
      const net = afterDisc / (1 + gstPct / 100);
      return round2(afterDisc); // grand for inclusive = afterDisc (tax embedded)
      // eslint-disable-next-line no-unreachable
      tax = afterDisc - net;
    } else {
      tax = afterDisc * gstPct / 100;
    }
    return round2(afterDisc + tax);
  };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const delLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, x) => x !== i) }));

  const openBatchEntry = async (i) => {
    const l = form.lines[i];
    if (!l.item_id) return toast.error("Pick an item first");
    if (!(Number(l.qty) > 0)) return toast.error("Enter quantity first");
    try {
      const { data } = await api.get(`/items/${l.item_id}/stock-batches`);
      setStockBatches(data);
      const it = items.find(x => x.id === l.item_id);
      if (it?.warranty === "Yes") {
        const serialMap = {};
        for (const b of data) {
          const { data: sers } = await api.get(`/warranties?item_id=${l.item_id}&batch_number=${encodeURIComponent(b.batch_number)}&status=Purchased`);
          serialMap[b.batch_number] = sers;
        }
        setWarrantySerials(serialMap);
      } else setWarrantySerials({});
      if (l.batches.length === 0 && data.length > 0) {
        setForm(f => {
          const lines = [...f.lines];
          lines[i] = { ...lines[i], batches: [] };
          return { ...f, lines };
        });
      }
      setBatchIdx(i);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleBatchPick = (i, srcBatch, checked) => setForm(f => {
    const lines = [...f.lines];
    let batches = [...lines[i].batches];
    if (checked) {
      const allocated = batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
      const remaining = Math.max(0, (Number(lines[i].qty) || 0) - allocated);
      const allocQty = Math.min(Number(srcBatch.qty_remaining) || 0, remaining);
      const autoSerials = (warrantySerials[srcBatch.batch_number] || [])
        .filter(s => !batches.some(b => b.serial_numbers.includes(s.serial_number)))
        .slice(0, allocQty)
        .map(s => s.serial_number);
      batches.push({ ...emptyBatchPick, batch_id: srcBatch.id,
        batch_number: srcBatch.batch_number, original_batch_number: srcBatch.batch_number,
        mrp: srcBatch.mrp, per_unit_cost: srcBatch.per_unit_cost,
        cgst: srcBatch.cgst, sgst: srcBatch.sgst,
        expiry_date: srcBatch.expiry_date || "",
        available: srcBatch.qty_remaining, qty: allocQty, serial_numbers: autoSerials });
    } else {
      batches = batches.filter(b => b.batch_id !== srcBatch.id);
    }
    lines[i] = { ...lines[i], batches };
    return { ...f, lines };
  });
  const setBatchField = (i, bId, k, v) => setForm(f => {
    const lines = [...f.lines];
    const batches = lines[i].batches.map(b => b.batch_id === bId ? { ...b, [k]: v } : b);
    lines[i] = { ...lines[i], batches };
    return { ...f, lines };
  });
  const toggleSerial = (i, bId, sn) => setForm(f => {
    const lines = [...f.lines];
    const batches = lines[i].batches.map(b => {
      if (b.batch_id !== bId) return b;
      const list = b.serial_numbers.includes(sn)
        ? b.serial_numbers.filter(x => x !== sn)
        : [...b.serial_numbers, sn];
      return { ...b, serial_numbers: list };
    });
    lines[i] = { ...lines[i], batches };
    return { ...f, lines };
  });

  const validateBatches = (i) => {
    const l = form.lines[i];
    const it = items.find(x => x.id === l.item_id);
    const target = Number(l.qty) || 0;
    if (l.batches.length === 0) return "Select at least one batch to sell from";
    const sum = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
    for (const b of l.batches) {
      if (!(Number(b.qty) > 0)) return `Batch ${b.batch_number} allocated 0 — sale quantity already fully covered by other batches`;
      if (Number(b.qty) > Number(b.available)) return `Sell qty ${b.qty} exceeds available ${b.available} in batch ${b.batch_number}`;
      if (!b.batch_number || !String(b.batch_number).trim()) return "Batch number cannot be empty";
      if (it?.warranty === "Yes") {
        if (b.serial_numbers.length !== Number(b.qty)) return `Select exactly ${b.qty} serial(s) for batch ${b.batch_number} (have ${b.serial_numbers.length})`;
      }
    }
    if (sum !== target) return `Total batch qty ${sum} must equal item qty ${target}`;
    return null;
  };

  const forwardFromBatch = async () => {
    const err = validateBatches(batchIdx);
    if (err) return toast.error(err);
    // Update Item Master if MRP/CGST/SGST/part_no changed vs first picked batch
    const l = form.lines[batchIdx];
    const it = items.find(x => x.id === l.item_id);
    const firstB = l.batches[0];
    const patch = {};
    if (Number(firstB.mrp) && Number(firstB.mrp) !== Number(it.mrp)) patch.mrp = Number(firstB.mrp);
    if (form.tax_type === "GST") {
      if (Number(firstB.cgst) !== Number(it.cgst)) patch.cgst = Number(firstB.cgst);
      if (Number(firstB.sgst) !== Number(it.sgst)) patch.sgst = Number(firstB.sgst);
    }
    if (l.part_number_override && l.part_number_override !== it.part_number) patch.part_number = l.part_number_override;
    if (l.hsn_code_override && l.hsn_code_override !== it.hsn_code) patch.hsn_code = l.hsn_code_override;
    if (Object.keys(patch).length) {
      try { await api.patch(`/items/${l.item_id}`, { ...it, ...patch }); const { data } = await api.get("/items?scope=all"); setItems(data); } catch {}
    }
    setForm(f => {
      const lines = [...f.lines];
      const line = lines[batchIdx];
      const sumG = line.batches.reduce((a, b) => a + (Number(b.mrp) || 0) * (Number(b.qty) || 0), 0);
      const sumQ = line.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
      const lastB = line.batches[line.batches.length - 1] || {};
      lines[batchIdx] = { ...line, batches_filled: true,
        rate: sumQ ? round2(sumG / sumQ) : line.rate,
        cgst: Number(lastB.cgst) || line.cgst, sgst: Number(lastB.sgst) || line.sgst };
      return { ...f, lines };
    });
    const next = batchIdx + 1;
    setBatchIdx(null);
    if (next < form.lines.length) {
      toast.success(`Batch details saved — next item`);
      setTimeout(() => { const el = document.querySelector(`[data-testid="line-item-${next}"]`); if (el) el.click(); }, 60);
    } else {
      toast.success("All batches saved — add next item or save");
      setTimeout(() => { const el = document.querySelector(`[data-testid="add-line-btn"]`); if (el) el.focus(); }, 60);
    }
  };

  const totals = useMemo(() => {
    let net = 0, cgst = 0, sgst = 0;
    form.lines.forEach(l => {
      if (!l.batches_filled) return;
      const gross = (Number(l.rate) || 0) * (Number(l.qty) || 0);
      const d = gross * (Number(l.discount_pct) || 0) / 100;
      const afterDisc = Math.max(0, gross - d);
      const gstPct = form.tax_type === "GST" ? (Number(l.cgst) || 0) + (Number(l.sgst) || 0)
                   : form.tax_type === "IGST" ? Number(l.igst) || 0 : 0;
      let n, c, s;
      if (form.bill_calc === "Inclusive" && gstPct > 0) {
        n = afterDisc / (1 + gstPct / 100);
        const t = afterDisc - n;
        c = form.tax_type === "GST" && gstPct > 0 ? t * (Number(l.cgst) || 0) / gstPct : t;
        s = form.tax_type === "GST" && gstPct > 0 ? t - c : 0;
      } else {
        n = afterDisc;
        c = form.tax_type === "GST" ? afterDisc * (Number(l.cgst) || 0) / 100 : (form.tax_type === "IGST" ? afterDisc * (Number(l.igst) || 0) / 100 : 0);
        s = form.tax_type === "GST" ? afterDisc * (Number(l.sgst) || 0) / 100 : 0;
      }
      net += n; cgst += c; sgst += s;
    });
    const pre = net + cgst + sgst;
    const od = Number(form.overall_discount) || 0;
    const effDisc = form.overall_discount_type === "pct" ? round2(pre * od / 100) : od;
    return { net: round2(net), cgst: round2(cgst), sgst: round2(sgst), effDisc, total: round2(pre - effDisc) };
  }, [form.lines, form.tax_type, form.bill_calc, form.overall_discount, form.overall_discount_type]);

  // Restore minimised sale form state
  useEffect(() => {
    const p = consumeRestore("txn-sale");
    if (!p || p.type !== type) return;
    setEditId(p.editId || null);
    setForm(p.form);
    setOpen(true);
  }, [pending, type]); // eslint-disable-line

  const refOfSale = () => {
    if (editId) return form.invoice_number || "Edit";
    return form.party_name || (customers.find(c => c.id === form.party_id)?.name) || "New";
  };
  const isDirtySale = () => {
    const l0 = form.lines?.[0];
    return !!(form.party_id || form.party_name || form.invoice_number || form.notes || (l0 && l0.item_id));
  };
  const minimiseSale = () => {
    minimize({ kind: "txn-sale", label: `${editId ? "Edit" : "New"} ${label || "Sale"}`,
               ref: refOfSale(), state: { type, form, editId },
               path: `/transactions?type=${type}`, dirty: isDirtySale() });
    setOpen(false); setBatchIdx(null);
    toast.success("Minimised to taskbar");
  };

  const openNew = () => { setEditId(null); setCustInfo(null); setBatchIdx(null); setForm(emptyForm()); setOpen(true); };
  // Deep-link edit: ?edit=<id> opens the form pre-filled (used by Transaction Master)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const eid = sp.get("edit");
    if (!eid) return;
    (async () => {
      try { const res = await api.get(`/transactions/${eid}`); openEdit(res.data); }
      catch (e) { toast.error(formatErr(e)); }
      sp.delete("edit");
      window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
    })();
  }, []); // eslint-disable-line
  const openEdit = (t) => {
    setEditId(t.id); setBatchIdx(null);
    const lines = (t.lines || []).map(l => ({
      item_id: l.item_id, qty: l.qty, rate: l.rate || 0, discount_pct: l.discount_pct || 0,
      cgst: l.cgst ?? 9, sgst: l.sgst ?? 9, igst: 0,
      batches: (l.batches || []).map(b => ({ ...emptyBatchPick, ...b, available: (Number(b.qty) || 0), serial_numbers: b.serial_numbers || [] })),
      batches_filled: true,
    }));
    setForm({ party_id: t.party_id || "", party_name: t.party_name || "", party_phone: t.party_phone || "",
      txn_date: t.txn_date || todayISO(), rate_type: t.rate_type || "mrp", party_address: t.party_address || "",
      invoice_number: t.invoice_number || "", invoice_override: !!t.invoice_number,
      tax_type: t.tax_type || "GST", bill_calc: t.bill_calc || "Exclusive",
      store_id: t.store_id || "", lines,
      overall_discount: t.overall_discount || 0, overall_discount_type: t.overall_discount_type || "amt", notes: t.notes || "" });
    if (t.party_id) pickCustomer(t.party_id);
    setOpen(true);
  };

  const allBatchesFilled = form.lines.every(l => l.item_id && Number(l.qty) > 0 && l.batches_filled);

  const save = () => {
    if (!form.party_id) return toast.error("Customer is required");
    if (form.lines.some(l => !l.item_id)) return toast.error("Pick items in every line");
    const useBatches = appSettings?.batch_entry_for_sale !== false;
    if (useBatches) {
      for (let i = 0; i < form.lines.length; i++) {
        if (!form.lines[i].batches_filled) return toast.error(`Enter batch details for item line ${i + 1} before saving`);
        const err = validateBatches(i);
        if (err) return toast.error(`Line ${i + 1}: ${err}`);
      }
    }
    if (appSettings?.auto_payment_panel === false) confirmSave([]);
    else setPayOpen(true);
  };

  const confirmSave = async (payRows) => {
    setSaving(true);
    try {
      const payload = {
        type, party_type: "customer", party_id: form.party_id,
        party_name: form.party_name || null, party_phone: form.party_phone || null,
        invoice_number: (form.invoice_override && form.invoice_number) ? form.invoice_number : null,
        tax_type: form.tax_type, bill_calc: form.bill_calc,
        txn_date: form.txn_date || null, rate_type: form.rate_type || null, party_address: form.party_address || null,
        store_id: form.store_id || null, notes: form.notes || null,
        overall_discount: Number(form.overall_discount) || 0, overall_discount_type: form.overall_discount_type,
        lines: form.lines.map(l => ({
          item_id: l.item_id, qty: Number(l.qty), rate: Number(l.rate) || 0, discount_pct: Number(l.discount_pct) || 0,
          batches: l.batches.map(b => ({
            batch_id: b.batch_id, original_batch_number: b.original_batch_number,
            batch_number: String(b.batch_number).trim(),
            qty: Number(b.qty), mrp: Number(b.mrp) || 0,
            per_unit_cost: Number(b.per_unit_cost) || 0,
            cgst: Number(b.cgst) || 0, sgst: Number(b.sgst) || 0, igst: Number(b.igst) || 0,
            expiry_date: b.expiry_date || null,
            serial_numbers: (b.serial_numbers || []),
          })),
        })),
      };
      let tid = editId, saved = null;
      if (editId) { const r = await api.patch(`/transactions/${editId}`, payload); saved = r.data; }
      else { const r = await api.post("/transactions", payload); saved = r.data; tid = r.data.id; }
      for (const p of payRows) await api.post(`/transactions/${tid}/payment`, { amount: Number(p.amount), method: p.mode });
      toast.success(editId ? "Sale updated" : "Sale saved");
      setPayOpen(false); setOpen(false); setBatchIdx(null); load();
      if (tid) { setSavedDoc({ id: tid, number: saved?.invoice_number || saved?.txn_number || "" }); if (appSettings?.auto_print_after_save) openTxnPdf(tid); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this sale? Stock, batches, warranties and payments will be reversed.")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openPayment = async (t) => {
    setPayDlg(t);
    setPayForm({ amount: t.outstanding || 0, method: "cash", note: "", paid_on: todayISO() });
    try { const { data } = await api.get(`/transactions/${t.id}/payments`); setPayments(data); }
    catch { setPayments([]); }
  };
  const savePayment = async () => {
    try {
      await api.post(`/transactions/${payDlg.id}/payment`, { ...payForm, amount: Number(payForm.amount) });
      toast.success("Payment recorded"); setPayDlg(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.altKey && (e.key === "4" || e.code === "Digit4")) {
        e.preventDefault();
        const lineEl = e.target && e.target.closest ? e.target.closest('[data-testid^="line-"]') : null;
        const lm = lineEl ? (lineEl.getAttribute("data-testid") || "").match(/(\d+)$/) : null;
        if (!lm) return;
        const iid = formRef.current.lines[Number(lm[1])]?.item_id;
        if (!iid) { toast.error("Pick an item in this line first"); return; }
        setRegItemId(iid);
        return;
      }
      if (regItemId) { if (e.key === "Escape") { e.preventDefault(); setRegItemId(null); } return; }
      if (payOpen) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); document.querySelector('[data-testid="pay-save-btn"]:not([disabled])')?.click(); }
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); setBatchIdx(null); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (batchIdx !== null) forwardFromBatch();
        else document.querySelector('[data-testid="save-sale-btn"]:not([disabled])')?.click();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, batchIdx, payOpen, regItemId]); // eslint-disable-line

  // Auto-focus Done/Forward once allocated qty equals the line's sale qty (serials matched for warranty items)
  useEffect(() => {
    if (batchIdx === null) return;
    const l = form.lines[batchIdx];
    if (!l || !l.batches.length) return;
    const target = Number(l.qty) || 0;
    const sum = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
    if (!target || sum !== target) return;
    const it = items.find(x => x.id === l.item_id);
    if (it?.warranty === "Yes" && l.batches.some(b => b.serial_numbers.length !== Number(b.qty))) return;
    const fwd = document.querySelector('[data-testid="batch-forward"]');
    if (!fwd || document.activeElement === fwd) return;
    const t = setTimeout(() => fwd.focus(), 130);
    return () => clearTimeout(t);
  }, [form.lines, batchIdx]); // eslint-disable-line

  const batchLine = batchIdx !== null ? form.lines[batchIdx] : null;
  const batchItem = batchLine ? items.find(x => x.id === batchLine.item_id) : null;

  const handleEnterNav = createKeyboardNav({
    selector: 'input:not([disabled]), [role="combobox"], [data-line-amt], button[data-testid^="enter-batch-"]:not([disabled]), button[data-testid="add-line-btn"], [data-net-amount]',
    endTarget: true,
    skipInside: '[data-testid="batch-entry-page"], [data-testid="payment-panel"], [data-testid="item-register-overlay"]',
    addLineBtn: true,
  });
  const handleBatchEnterNav = (e) => {
    if (e.key === "Backspace") { backspacePrev(e, e.currentTarget, 'input:not([disabled]), button[role="checkbox"], [data-batch-stop]'); return; }
    if (e.key.startsWith("Arrow")) { arrowNav(e, e.currentTarget, 'input:not([disabled]), button[role="checkbox"], [data-batch-stop]'); return; }
    if (e.key !== "Enter") return;
    const t = e.target;
    if (t.tagName === "TEXTAREA") return;
    const container = e.currentTarget;
    const role = t.getAttribute("role");
    // plain buttons (back / forward) keep native Enter = click
    if (t.tagName === "BUTTON" && role !== "checkbox") return;
    e.preventDefault();
    e.stopPropagation();
    const sel = 'input:not([disabled]), button[role="checkbox"], [data-batch-stop]';
    if (role === "checkbox") {
      t.click();
      setTimeout(() => {
        const f2 = [...container.querySelectorAll(sel)].filter(el => el.offsetParent !== null);
        const i2 = f2.indexOf(t);
        const nx = f2[i2 + 1];
        if (nx) nx.focus(); else container.querySelector('[data-testid="batch-forward"]')?.focus();
      }, 60);
      return;
    }
    const fields = [...container.querySelectorAll(sel)].filter(el => el.offsetParent !== null);
    const idx = fields.indexOf(t);
    if (idx === -1) return;
    const next = fields[idx + 1];
    if (next) { next.focus(); if (next.select) { try { next.select(); } catch {} } }
    else forwardFromBatch();
  };

  return (
    <div>
      {!open && (<>
      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3" data-testid="sale-filter-bar" onKeyDownCapture={handleSimpleFormNav}>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</Label>
          <Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</Label>
          <Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <Button data-testid="today-btn" size="sm" variant="outline" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="rounded-sm text-xs uppercase tracking-wider">Today</Button>
        <Button data-testid="all-dates-btn" size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-sm text-xs uppercase tracking-wider">All Dates</Button>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search (Invoice #, Customer)</Label>
          <Input data-testid="sale-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <Button data-testid="sale-export-pdf-btn" variant="outline" size="sm" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export PDF</Button>
          <Button data-testid="sale-print-list-btn" variant="outline" size="sm" onClick={printList} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
          <span className="text-xs text-muted-foreground pb-2" data-testid="sale-count">{list.length} record(s)</span>
          {canModify && (
            <Button data-testid="new-sale-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
              <Plus className="w-4 h-4 mr-1"/> New {label}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th rowSpan={2} className="text-left p-3 align-bottom">Invoice #</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Date</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Customer</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Mobile</th>
              <th rowSpan={2} className="text-right p-3 align-bottom">Items</th>
              <th colSpan={3} className="text-center p-2 border-l border-b border-border/60" data-testid="sale-amount-group-header">Amount</th>
              <th colSpan={4} className="text-center p-2 border-l border-b border-border/60" data-testid="sale-paid-group-header">Paid</th>
              <th rowSpan={2} className="text-right p-3 align-bottom border-l border-border/60">Balance</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Payment</th>
              <th rowSpan={2} className="text-right p-3 align-bottom">Actions</th>
            </tr>
            <tr>
              <th className="text-right px-3 pb-2 border-l border-border/60">Amount</th>
              <th className="text-right px-3 pb-2">GST</th>
              <th className="text-right px-3 pb-2">Total</th>
              <th className="text-right px-3 pb-2 border-l border-border/60">Cash</th>
              <th className="text-right px-3 pb-2">PhonePay</th>
              <th className="text-right px-3 pb-2">Paytm</th>
              <th className="text-right px-3 pb-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={15} className="p-6 text-center text-muted-foreground">No {label.toLowerCase()} records</td></tr>}
            {pg.pageItems.map(t => {
              const gst = (t.cgst_total || 0) + (t.sgst_total || 0);
              return (
              <tr key={t.id} data-testid={`sale-row-${t.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{t.invoice_number || t.txn_number}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-xs">{t.party_name || "-"}</td>
                <td className="p-3 text-xs font-mono" data-testid={`sale-mobile-${t.id}`}>{t.party_phone || "-"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`sale-items-${t.id}`}>{t.lines.length}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`sale-amount-${t.id}`}>{inr((t.total || 0) - gst)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`sale-gst-${t.id}`}>{inr(gst)}</td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`sale-total-${t.id}`}>{inr(t.total || 0)}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`sale-cash-${t.id}`}>{inr(t.paid_cash || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`sale-phonepay-${t.id}`}>{inr(t.paid_phonepay || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`sale-paytm-${t.id}`}>{inr(t.paid_paytm || 0)}</td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`sale-paid-${t.id}`}>{inr(t.paid_amount || 0)}</td>
                <td className={`p-3 text-right font-mono text-xs border-l border-border/60 ${(t.outstanding || 0) > 0 ? "text-destructive" : "text-green-500"}`} data-testid={`sale-balance-${t.id}`}>{inr(t.outstanding || 0)}</td>
                <td className="p-3"><span data-testid={`pay-status-${t.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[t.payment_status] || statusColor.Unpaid}`}>{PAY_LABEL[t.payment_status] || t.payment_status}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {canModify && t.payment_status !== "Paid" && (
                    <Button data-testid={`record-pay-${t.id}`} size="sm" variant="ghost" onClick={() => openPayment(t)} className="h-7 text-xs"><Wallet className="w-3 h-3 mr-1"/>Pay</Button>
                  )}
                  {canModify && <Button data-testid={`edit-sale-${t.id}`} size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canModify && <Button data-testid={`del-sale-${t.id}`} size="sm" variant="ghost" onClick={() => del(t.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
              );
            })}
            {list.length > 0 && (
              <tr data-testid="sale-total-row" className="border-t-2 border-primary/40 bg-secondary/60 font-bold align-top">
                <td className="p-3 text-[10px] uppercase tracking-[0.2em] text-primary" colSpan={4}>Totals · {list.length} records</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="sale-total-items">{txnTotals.items}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="sale-total-amount">{inr(txnTotals.amount)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="sale-total-gst">{inr(txnTotals.gst)}</td>
                <td className="p-3 text-right font-mono" data-testid="sale-total-total">{inr(txnTotals.total)}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="sale-total-cash">{inr(txnTotals.cash)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="sale-total-phonepay">{inr(txnTotals.phonepay)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="sale-total-paytm">{inr(txnTotals.paytm)}</td>
                <td className="p-3 text-right font-mono" data-testid="sale-total-paid">{inr(txnTotals.paid)}</td>
                <td className="p-3 text-right font-mono border-l border-border/60" data-testid="sale-total-balance">{inr(txnTotals.balance)}</td>
                <td colSpan={2}/>
              </tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
      </>)}

      {/* List print view (only rendered in list mode) */}
      {!open && (
        <div className="hidden" id="print-sale-txns">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-lg font-bold">{org?.business_name || "SynqMoto ERP Workshop"}</div>
              <div className="text-xs">{org?.establishment_name || org?.branch_name || ""}</div>
            </div>
            <div className="text-right text-xs">
              <div className="font-bold">{label} Report</div>
              <div>Report Date: {new Date().toISOString().slice(0, 10)}</div>
            </div>
          </div>
          <div className="text-xs mb-3 border-b border-black pb-2">Filters — {filterSummary} · Rows: {list.length}</div>
          <table className="w-full border-collapse" style={{ fontSize: "8px" }}>
            <thead>
              <tr>
                <th rowSpan={2} className="border border-black text-left p-1">Invoice #</th>
                <th rowSpan={2} className="border border-black text-left p-1">Date</th>
                <th rowSpan={2} className="border border-black text-left p-1">Customer</th>
                <th rowSpan={2} className="border border-black text-left p-1">Mobile</th>
                <th rowSpan={2} className="border border-black text-right p-1">Items</th>
                <th colSpan={3} className="border border-black text-center p-1">Amount</th>
                <th colSpan={4} className="border border-black text-center p-1">Paid</th>
                <th rowSpan={2} className="border border-black text-right p-1">Balance</th>
                <th rowSpan={2} className="border border-black text-left p-1">Payment</th>
              </tr>
              <tr>
                <th className="border border-black text-right p-1">Amount</th>
                <th className="border border-black text-right p-1">GST</th>
                <th className="border border-black text-right p-1">Total</th>
                <th className="border border-black text-right p-1">Cash</th>
                <th className="border border-black text-right p-1">PhonePay</th>
                <th className="border border-black text-right p-1">Paytm</th>
                <th className="border border-black text-right p-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {list.map(t => {
                const gst = (t.cgst_total || 0) + (t.sgst_total || 0);
                return (
                <tr key={t.id}>
                  <td className="border border-black p-1">{t.invoice_number || t.txn_number}</td>
                  <td className="border border-black p-1">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="border border-black p-1">{t.party_name || "-"}</td>
                  <td className="border border-black p-1">{t.party_phone || "-"}</td>
                  <td className="border border-black p-1 text-right">{t.lines?.length || 0}</td>
                  <td className="border border-black p-1 text-right">{inr((t.total || 0) - gst)}</td>
                  <td className="border border-black p-1 text-right">{inr(gst)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.total || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.paid_cash || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.paid_phonepay || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.paid_paytm || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.paid_amount || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(t.outstanding || 0)}</td>
                  <td className="border border-black p-1">{PAY_LABEL[t.payment_status] || t.payment_status}</td>
                </tr>
                );
              })}
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={4}>TOTALS · {list.length} records</td>
                <td className="border border-black p-1 text-right">{txnTotals.items}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.amount)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.gst)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.total)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.cash)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.phonepay)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.paytm)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.paid)}</td>
                <td className="border border-black p-1 text-right">{inr(txnTotals.balance)}</td>
                <td className="border border-black p-1"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <style>{`
        @media print {
          body.printing-sale-list * { visibility: hidden; }
          body.printing-sale-list #print-sale-txns, body.printing-sale-list #print-sale-txns * { visibility: visible; }
          body.printing-sale-list #print-sale-txns { position: absolute; left: 0; top: 0; width: 100%; display: block !important; background: #fff; color: #000; }
        }
      `}</style>

      {/* Full-page form — main form vs batch entry page */}
      {open && (
      <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="sale-form-page" onKeyDownCapture={handleEnterNav}>
          {batchIdx === null ? (
            <>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-center gap-3">
                  <Button type="button" data-testid="form-back-btn" variant="outline" onClick={() => { setOpen(false); setBatchIdx(null); }} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
                  <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} {label}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" data-testid="minimise-sale-btn" variant="outline" onClick={minimiseSale} className="rounded-sm text-xs uppercase tracking-wider"><Minimize2 className="w-3 h-3 mr-1"/>Minimise</Button>
                  <Button type="button" data-testid="form-cancel-btn" variant="ghost" onClick={() => { setOpen(false); setBatchIdx(null); }} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,1fr))] gap-3">
                  {/* Line 1: Customer (wide), Mobile Number, Date, Invoice Number (compact) */}
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={form.party_id} onValueChange={pickCustomer}>
                        <SelectTrigger data-testid="sale-customer" className="rounded-sm bg-secondary border-border flex-1 min-w-0"><SelectValue placeholder="Select customer"/></SelectTrigger>
                        <SelectContent>{customers.map(v => <SelectItem key={v.id} value={v.id}>{v.name} · {v.phone}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" data-testid="add-customer-btn" variant="outline" size="icon" onClick={() => setAddCustOpen(true)} title="New Customer" className="rounded-sm h-9 w-9 shrink-0"><Plus className="w-4 h-4"/></Button>
                    </div>
                    {custInfo && (
                      <div data-testid="customer-info" className="mt-2 text-[11px] font-mono border border-border bg-secondary/40 rounded-sm p-2 space-y-0.5">
                        {custInfo.gstin && <div><span className="text-muted-foreground">GSTIN:</span> {custInfo.gstin}</div>}
                        <div><span className="text-muted-foreground">Outstanding:</span> <span className="text-primary">{inr(custInfo.outstanding)}</span></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile Number</Label>
                    <Input data-testid="sale-mobile" value={form.party_phone} onChange={(e) => onMobileChange(e.target.value)} placeholder="Search by mobile…" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                    <Input type="date" data-testid="sale-date" value={form.txn_date} onChange={(e) => setForm(f => ({ ...f, txn_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Invoice Number</Label>
                    <div className="flex gap-2 mt-1 items-center">
                      <Input data-testid="sale-invoice-number" value={form.invoice_number} disabled={!form.invoice_override && !editId} onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="Auto-generated" className="rounded-sm bg-secondary border-border font-mono min-w-0"/>
                      {canModify && (
                        <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground shrink-0" title="Manual override (Owner/Manager)">
                          <Checkbox data-testid="invoice-override-toggle" checked={form.invoice_override} onCheckedChange={(v) => setForm(f => ({ ...f, invoice_override: !!v }))}/>
                          Manual
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-[minmax(0,1.2fr)_repeat(4,minmax(0,1fr))] gap-3">
                  {/* Line 2: Address+Rate align under Customer; Tax/Store/Bill Calc under Mobile/Date/Invoice */}
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                    <Input data-testid="sale-address" value={form.party_address} onChange={(e) => setForm(f => ({ ...f, party_address: e.target.value }))} placeholder="Auto-filled from customer" className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate (Rate Master)</Label>
                    <Select value={form.rate_type} onValueChange={applyRateType}>
                      <SelectTrigger data-testid="sale-rate-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mrp">MRP</SelectItem>
                        <SelectItem value="wholesale_rate">Wholesale Rate</SelectItem>
                        <SelectItem value="purchase_rate">Purchase Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tax Type *</Label>
                    <Select value={form.tax_type} onValueChange={(v) => setForm(f => ({ ...f, tax_type: v }))}>
                      <SelectTrigger data-testid="sale-tax-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GST">GST (CGST + SGST)</SelectItem>
                        <SelectItem value="IGST">IGST</SelectItem>
                        <SelectItem value="Exempt">Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
                    <Select value={form.store_id} onValueChange={(v) => setForm(f => ({ ...f, store_id: v }))}>
                      <SelectTrigger data-testid="sale-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Optional"/></SelectTrigger>
                      <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bill Calculation *</Label>
                    <Select value={form.bill_calc} onValueChange={(v) => setForm(f => ({ ...f, bill_calc: v }))}>
                      <SelectTrigger data-testid="sale-bill-calc" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Exclusive">Exclusive of Tax</SelectItem>
                        <SelectItem value="Inclusive">Inclusive of Tax</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="grid grid-cols-[minmax(160px,1.7fr)_60px_100px_88px_84px_64px_64px_64px_86px_118px_36px] gap-2 items-end px-[9px] text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <div className="text-center">Item *</div>
                    <div className="text-center">Qty *</div>
                    <div className="text-center">Part No</div>
                    <div className="text-center">HSN</div>
                    <div className="text-center">MRP</div>
                    <div className="text-center">Disc %</div>
                    {form.tax_type === "GST" && <><div className="text-center">CGST %</div><div className="text-center">SGST %</div></>}
                    {form.tax_type === "IGST" && <div className="text-center col-span-2">IGST %</div>}
                    {form.tax_type === "Exempt" && <div className="col-span-2 text-center">Exempt</div>}
                    <div className="text-center">Amount</div>
                    <div></div><div></div>
                  </div>
                  {form.lines.map((l, i) => {
                    const it = items.find(x => x.id === l.item_id);
                    const sumQty = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
                    return (
                      <div key={i} data-testid={`line-${i}`} data-kb-row={i} className={`border rounded-sm p-2 ${l.batches_filled ? "border-green-500/40 bg-green-500/5" : "border-border bg-secondary/20"}`}>
                        <div className="grid grid-cols-[minmax(160px,1.7fr)_60px_100px_88px_84px_64px_64px_64px_86px_118px_36px] gap-2 items-center">
                          <ItemCombobox
                            items={items} value={l.item_id} testId={`line-item-${i}`}
                            labelFn={(x) => `${x.item_code ? x.item_code + " · " : ""}${x.item_name} · MRP ₹${x.mrp}${x.warranty === "Yes" ? " · Warranty" : ""}`}
                            onSelect={(x) => setLineItem(i, x.id)}
                            afterSelect={() => setTimeout(() => document.querySelector(`[data-testid="line-qty-${i}"]`)?.focus(), 50)}
                          />
                          <Input data-testid={`line-qty-${i}`} type="number" min="1" value={l.qty} onChange={(e) => setLineQty(i, e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          <Input data-testid={`line-partno-${i}`} value={it?.part_number || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground"/>
                          <Input data-testid={`line-hsn-${i}`} value={it?.hsn_code || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground"/>
                          <Input data-testid={`line-mrp-${i}`} type="number" value={l.rate} onChange={(e) => setLineField(i, "rate", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          <Input data-testid={`line-disc-${i}`} type="number" min="0" max="100" value={l.discount_pct} onChange={(e) => setLineField(i, "discount_pct", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          {form.tax_type === "GST" && <>
                            <Input data-testid={`line-cgst-${i}`} type="number" value={l.cgst} onChange={(e) => setLineField(i, "cgst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                            <Input data-testid={`line-sgst-${i}`} type="number" value={l.sgst} onChange={(e) => setLineField(i, "sgst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          </>}
                          {form.tax_type === "IGST" && (
                            <Input data-testid={`line-igst-${i}`} type="number" value={l.igst} onChange={(e) => setLineField(i, "igst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right col-span-2"/>
                          )}
                          {form.tax_type === "Exempt" && <div className="col-span-2 text-right text-[10px] uppercase text-muted-foreground">—</div>}
                          <div data-testid={`line-amount-${i}`} data-line-amt tabIndex={-1} className="text-right font-mono text-xs text-primary rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">{inr(lineAmount(l))}</div>
                          <Button type="button" data-testid={`enter-batch-${i}`} onClick={() => openBatchEntry(i)}
                            disabled={!l.item_id || !(Number(l.qty) > 0)}
                            className={`rounded-sm text-[10px] uppercase tracking-wider h-9 whitespace-nowrap ${l.batches_filled ? "bg-green-600 hover:bg-green-600 text-white" : "bg-primary text-primary-foreground"}`}>
                            <Layers className="w-3 h-3 mr-1"/>{l.batches_filled ? "Edit Batch" : "Enter Batch"}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => delLine(i)} disabled={form.lines.length === 1} className="h-9 w-9 p-0 text-destructive"><Trash2 className="w-4 h-4"/></Button>
                        </div>
                        {l.batches_filled && (
                          <div data-testid={`line-summary-${i}`} className="mt-2 text-xs font-mono text-green-500 flex items-center gap-3 flex-wrap">
                            <span>{l.batches.length} batch(es) · {sumQty} unit(s)</span>
                            <span className="text-muted-foreground">{l.batches.map(b => b.batch_number).join(", ")}</span>
                            {it?.warranty === "Yes" && <span className="text-primary">{l.batches.reduce((a, b) => a + b.serial_numbers.length, 0)} serial(s) selected</span>}
                          </div>
                        )}
                        {it && !l.batches_filled && it.warranty === "Yes" && (
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-primary">Warranty item — serial numbers must be picked in batch entry</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Button type="button" data-testid="add-line-btn" size="sm" variant="outline" onClick={addLine} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Item Line</Button>

                <div className="grid grid-cols-2 gap-4 border-t border-border pt-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label>
                    <Textarea data-testid="sale-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Net Amount</span><span className="font-mono" data-testid="totals-net">{inr(totals.net)}</span></div>
                    {form.tax_type === "GST" && <>
                      <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono" data-testid="totals-cgst">{inr(totals.cgst)}</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono" data-testid="totals-sgst">{inr(totals.sgst)}</span></div>
                    </>}
                    {form.tax_type === "IGST" && <div className="flex justify-between text-muted-foreground"><span>IGST</span><span className="font-mono" data-testid="totals-igst">{inr(totals.cgst)}</span></div>}
                    <div className="flex justify-between items-center">
                      <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Overall Discount</span>
                      <div className="flex items-center gap-1">
                        {form.overall_discount_type === "pct" && Number(form.overall_discount) > 0 && <span data-testid="overall-discount-amt" className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">= {inr(totals.effDisc)}</span>}
                        <Input data-testid="overall-discount" data-end-target type="number" min="0" {...(form.overall_discount_type === "pct" ? { max: "100" } : {})} value={form.overall_discount} onChange={(e) => setForm(f => ({ ...f, overall_discount: e.target.value }))} className="w-24 h-8 rounded-sm bg-secondary border-border text-right"/>
                        <div className="flex border border-border rounded-sm overflow-hidden shrink-0">
                          <button type="button" data-testid="disc-type-amt" onClick={() => setForm(f => ({ ...f, overall_discount_type: "amt" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "amt" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>₹</button>
                          <button type="button" data-testid="disc-type-pct" onClick={() => setForm(f => ({ ...f, overall_discount_type: "pct" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "pct" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>%</button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2 items-center">
                      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total</span>
                      <span data-testid="totals-grand" data-net-amount tabIndex={-1} className="font-display font-black text-2xl text-primary rounded-sm px-1 focus:outline-none focus:ring-2 focus:ring-primary">{inr(totals.total)}</span>
                    </div>
                  </div>
                </div>

                {!allBatchesFilled && (
                  <div data-testid="save-blocked-hint" className="text-[11px] uppercase tracking-wider text-yellow-500 border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-2">
                    Enter batch details for all item lines before saving
                  </div>
                )}
                <Button data-testid="save-sale-btn" onClick={save} disabled={!allBatchesFilled} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update Sale" : `Save ${label}`}</Button>
              </div>
            </>
          ) : (
            <div data-testid="batch-entry-page" onKeyDownCapture={handleBatchEnterNav}>
              <div className="flex items-center gap-3 border-b border-border pb-3">
                <Button type="button" data-testid="batch-back" size="sm" variant="ghost" onClick={() => setBatchIdx(null)} className="h-8 w-8 p-0"><ArrowLeft className="w-4 h-4"/></Button>
                <h2 className="font-display uppercase text-lg">Select Batch — {batchItem?.item_name}</h2>
                <span className="text-muted-foreground text-xs font-normal">Line {batchIdx + 1} of {form.lines.length}</span>
              </div>
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-4 text-xs font-mono border border-border bg-secondary/40 rounded-sm p-2 flex-wrap">
                  <span className="text-muted-foreground">Sale Qty (from line): <span className="text-foreground font-bold" data-testid="batch-sale-qty">{batchLine?.qty}</span></span>
                  <span className="text-muted-foreground">Selected: <span className={batchLine?.batches.reduce((a,b)=>a+(Number(b.qty)||0),0) === Number(batchLine?.qty) ? "text-green-500" : "text-yellow-500"}>{batchLine?.batches.reduce((a,b)=>a+(Number(b.qty)||0),0)}</span></span>
                  <span className="text-muted-foreground">Tax: <span className="text-foreground">{form.tax_type} · {form.bill_calc}</span></span>
                  {batchItem?.warranty === "Yes" && <span className="text-primary uppercase tracking-wider text-[10px]">Warranty — tick serial numbers per batch</span>}
                </div>

                {stockBatches.length === 0 && (
                  <div data-testid="no-stock" className="border border-yellow-500/40 bg-yellow-500/5 text-yellow-500 rounded-sm p-4 text-center text-sm uppercase tracking-wider">No stock batches available for this item</div>
                )}

                {stockBatches.map(src => {
                  const pick = (batchLine.batches || []).find(b => b.batch_id === src.id);
                  const isPicked = !!pick;
                  const serials = (warrantySerials[src.batch_number] || []);
                  const takenElsewhere = new Set((batchLine.batches || []).filter(b => b.batch_id !== src.id).flatMap(b => b.serial_numbers));
                  return (
                    <div key={src.id} data-testid={`stock-batch-${src.id}`} className={`border rounded-sm p-3 ${isPicked ? "border-primary/50 bg-primary/5" : "border-border"}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <Checkbox data-testid={`pick-batch-${src.id}`} checked={isPicked} onCheckedChange={(v) => toggleBatchPick(batchIdx, src, !!v)}/>
                        <div className="flex-1 grid grid-cols-6 gap-2 text-xs">
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Batch #</div>
                            <div className="font-mono">{src.batch_number}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Part No</div>
                            <div className="font-mono">{batchItem?.part_number}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">HSN</div>
                            <div className="font-mono">{batchItem?.hsn_code || "-"}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">MRP</div>
                            <div className="font-mono">{inr(src.mrp)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">GST</div>
                            <div className="font-mono">{src.cgst}+{src.sgst}%</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Available</div>
                            <div className="font-mono text-primary">{src.qty_remaining}</div>
                          </div>
                        </div>
                      </div>
                      {isPicked && (
                        <div className="border-t border-border pt-2 space-y-2">
                          <div className="grid grid-cols-9 gap-2 items-end">
                            <div>
                              <Label className="text-[10px] uppercase text-muted-foreground">Batch # (edit)</Label>
                              <Input data-testid={`pick-batchno-${src.id}`} value={pick.batch_number} onChange={(e) => setBatchField(batchIdx, src.id, "batch_number", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs font-mono"/>
                            </div>
                            <div data-batch-stop data-testid={`pick-partno-${src.id}`} tabIndex={-1} className="rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">
                              <div className="text-[10px] uppercase text-muted-foreground">Part No</div>
                              <div className="font-mono text-xs pt-1.5">{batchItem?.part_number}</div>
                            </div>
                            <div data-batch-stop data-testid={`pick-hsn-${src.id}`} tabIndex={-1} className="rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">
                              <div className="text-[10px] uppercase text-muted-foreground">HSN</div>
                              <div className="font-mono text-xs pt-1.5">{batchItem?.hsn_code || "-"}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-muted-foreground">MRP</Label>
                              <Input data-testid={`pick-mrp-${src.id}`} type="number" value={pick.mrp} onChange={(e) => setBatchField(batchIdx, src.id, "mrp", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-muted-foreground">CGST %</Label>
                              <Input data-testid={`pick-cgst-${src.id}`} type="number" value={pick.cgst} onChange={(e) => setBatchField(batchIdx, src.id, "cgst", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-muted-foreground">SGST %</Label>
                              <Input data-testid={`pick-sgst-${src.id}`} type="number" value={pick.sgst} onChange={(e) => setBatchField(batchIdx, src.id, "sgst", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                            </div>
                            <div data-batch-stop data-testid={`pick-avail-${src.id}`} tabIndex={-1} className="rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">
                              <div className="text-[10px] uppercase text-muted-foreground">Available</div>
                              <div className="font-mono text-xs pt-1.5 text-primary">{src.qty_remaining}</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-muted-foreground">Sell Qty (auto)</Label>
                              <Input data-testid={`pick-qty-${src.id}`} type="number" value={pick.qty} readOnly tabIndex={-1} className="mt-1 rounded-sm bg-secondary/50 border-border h-8 text-xs text-right text-muted-foreground cursor-not-allowed"/>
                            </div>
                            <div className="text-[10px] uppercase text-muted-foreground text-right pb-1">Line total<br/><span className="text-foreground font-mono text-xs">{inr(Number(pick.mrp || 0) * Number(pick.qty || 0))}</span></div>
                          </div>
                          {batchItem?.warranty === "Yes" && (
                            <div className="border-t border-border pt-2">
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">Serial Numbers in stock ({serials.length}) — pick {pick.qty || 0}</div>
                              {serials.length === 0 && <div className="text-xs text-muted-foreground italic">No serials linked to this batch</div>}
                              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                                {serials.map(sn => {
                                  const disabledElsewhere = takenElsewhere.has(sn.serial_number);
                                  return (
                                    <label key={sn.id} className={`flex items-center gap-2 text-xs font-mono border border-border bg-secondary/30 rounded-sm p-1.5 ${disabledElsewhere ? "opacity-40" : ""}`}>
                                      <Checkbox data-testid={`serial-${src.id}-${sn.serial_number}`} disabled={disabledElsewhere} checked={pick.serial_numbers.includes(sn.serial_number)} onCheckedChange={() => toggleSerial(batchIdx, src.id, sn.serial_number)}/>
                                      <span>{sn.serial_number}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider">
                    {isRetail ? "Warranty will auto-activate for each selected serial" : "Serials will be marked Sold — Pending Warranty Activation"}
                  </span>
                  <Button data-testid="batch-forward" onClick={forwardFromBatch} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
                    {batchIdx + 1 < form.lines.length ? (<>Forward — Next Item <ArrowRight className="w-3 h-3 ml-1"/></>) : (<>Enter — Done <CornerDownLeft className="w-3 h-3 ml-1"/></>)}
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>
      )}

      {regItemId && (
        <div className="fixed inset-0 z-50 bg-background overflow-y-auto p-6" data-testid="item-register-overlay">
          <ItemRegisterView itemId={regItemId} onClose={() => setRegItemId(null)} />
        </div>
      )}

      <PaymentPanel open={payOpen} title={label} grandTotal={totals.total} saving={saving}
        onCancel={() => { setPayOpen(false); setTimeout(() => document.querySelector('[data-testid="save-sale-btn"]')?.focus(), 60); }}
        onConfirm={confirmSave} />

      <Dialog open={!!savedDoc} onOpenChange={(v) => { if (!v) setSavedDoc(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm text-center" onKeyDownCapture={handleSimpleFormNav}>
          <DialogHeader><DialogTitle className="font-display uppercase text-center">Save Confirmation</DialogTitle></DialogHeader>
          {savedDoc && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Invoice Number</div>
                <div className="font-mono font-bold text-xl text-primary mt-1" data-testid="saved-invoice-number">{savedDoc.number}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="print-invoice-btn" onClick={() => openTxnPdf(savedDoc.id)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/>Print Invoice</Button>
                <Button data-testid="new-transaction-btn" variant="outline" onClick={() => { setSavedDoc(null); openNew(); }} className="rounded-sm uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/>New Transaction</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addCustOpen} onOpenChange={setAddCustOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name *</Label><Input data-testid="cust-name" value={cust.name} onChange={(e) => setCust(s => ({ ...s, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone *</Label><Input data-testid="cust-phone" value={cust.phone} onChange={(e) => setCust(s => ({ ...s, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GSTIN</Label><Input data-testid="cust-gstin" value={cust.gstin} onChange={(e) => setCust(s => ({ ...s, gstin: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Input data-testid="cust-address" value={cust.address} onChange={(e) => setCust(s => ({ ...s, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <Button data-testid="save-customer-btn" onClick={saveCustomer} disabled={!cust.name || !cust.phone} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Customer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payDlg} onOpenChange={(v) => { if (!v) setPayDlg(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Record Payment</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3">
              <div className="text-xs font-mono border border-border rounded-sm p-2 bg-secondary/40 space-y-0.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span>{payDlg.invoice_number || payDlg.txn_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{payDlg.party_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{inr(payDlg.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span className="text-green-500">{inr(payDlg.paid_amount || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Outstanding</span><span className="text-primary">{inr(payDlg.outstanding || 0)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Amount *</Label>
                  <Input data-testid="pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm(p => ({ ...p, amount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm(p => ({ ...p, method: v }))}>
                    <SelectTrigger data-testid="pay-method" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {payments.length > 0 && (
                <div className="border-t border-border pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Previous Payments</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {payments.map(p => (
                      <div key={p.id} className="flex justify-between text-xs font-mono">
                        <span className="text-muted-foreground">{p.paid_on} · {p.method}</span>
                        <span>{inr(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button data-testid="save-payment-btn" onClick={savePayment} disabled={!(Number(payForm.amount) > 0)} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Payment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
